import { useRef, useState } from "react";
import { OptionBadge } from "@/components/badges";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, GripVertical, MoreVertical, EyeOff, Archive, ArchiveRestore, MessageSquarePlus } from "lucide-react";
import { PROVINCES, getDistricts } from "@/data/turkiye";
import { Button } from "@/components/ui/button";

/**
 * Interactive data grid.
 * - draggable headers (reorder)
 * - resizable columns (drag right border)
 * - inline text/number/date editing (click cell)
 * - select cells => colored badge + dropdown of options
 */
export default function DataGrid({
  columns,
  records,
  onReorder,
  onResize,
  onCellChange,
  onDeleteRow,
  onArchiveRow,
  onOpenNotes,
  onFillColumn,
  view,
  onHideColumn,
  onDeleteColumn,
  canManageCols,
  showFilters,
  filters,
  onFilterChange,
  canEdit,
  canDelete,
  selected,
  setSelected,
}) {
  const visible = columns.filter((c) => c.visible);
  const showActions = true;
  const [editing, setEditing] = useState(null); // {rowId, key}
  const dragKey = useRef(null);
  const resizeState = useRef(null);

  const startResize = (e, key, width) => {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = { key, startX: e.clientX, startW: width };
    document.body.classList.add("resizing");
    const onMove = (ev) => {
      const rs = resizeState.current;
      if (!rs) return;
      const w = Math.max(70, rs.startW + (ev.clientX - rs.startX));
      onResize(rs.key, w, false);
    };
    const onUp = () => {
      const rs = resizeState.current;
      if (rs) onResize(rs.key, null, true); // persist
      resizeState.current = null;
      document.body.classList.remove("resizing");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const allChecked = records.length > 0 && selected.length === records.length;
  const toggleAll = () =>
    setSelected(allChecked ? [] : records.map((r) => r.id));
  const toggleOne = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const renderCell = (rec, col) => {
    const val = rec.values?.[col.key] ?? "";

    // Türkiye il/ilçe bağımlı seçim
    if (col.key === "il" || col.key === "ilce") {
      const isEd = editing && editing.rowId === rec.id && editing.key === col.key;
      const opts = col.key === "il" ? PROVINCES : getDistricts(rec.values?.il);
      if (isEd && canEdit) {
        return (
          <select
            autoFocus
            defaultValue={val}
            className="w-full bg-background border border-primary rounded px-1 py-0.5 text-sm outline-none"
            onChange={(e) => {
              onCellChange(rec, col.key, e.target.value);
              setEditing(null);
            }}
            onBlur={() => setEditing(null)}
            data-testid={`cell-geo-${col.key}`}
          >
            <option value="">—</option>
            {opts.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      }
      return (
        <div
          className={`truncate ${canEdit ? "cursor-pointer" : ""}`}
          onClick={() => canEdit && setEditing({ rowId: rec.id, key: col.key })}
          title={String(val)}
        >
          {val !== "" ? val : <span className="text-muted-foreground/40">—</span>}
        </div>
      );
    }

    if (col.type === "select") {
      const opt = (col.options || []).find((o) => o.value === val);
      const isEditingSel = editing && editing.rowId === rec.id && editing.key === col.key;
      if (isEditingSel && canEdit) {
        return (
          <select
            autoFocus
            defaultValue={val}
            className="w-full bg-background border border-primary rounded px-1 py-0.5 text-sm outline-none"
            onChange={(e) => {
              onCellChange(rec, col.key, e.target.value);
              setEditing(null);
            }}
            onBlur={() => setEditing(null)}
            data-testid={`cell-select-${col.key}`}
          >
            <option value="">—</option>
            {(col.options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.value}
              </option>
            ))}
          </select>
        );
      }
      return (
        <button
          className="w-full text-left disabled:cursor-default"
          disabled={!canEdit}
          onClick={() => canEdit && setEditing({ rowId: rec.id, key: col.key })}
          data-testid={`cell-select-${col.key}`}
        >
          <OptionBadge value={val} color={opt?.color} />
        </button>
      );
    }

    const isEditing = editing && editing.rowId === rec.id && editing.key === col.key;
    if (isEditing && canEdit) {
      const inputType = col.type === "number" ? "number" : col.type === "date" ? "date" : "text";
      return (
        <input
          autoFocus
          type={inputType}
          defaultValue={val}
          className="w-full bg-background border border-primary rounded px-1.5 py-0.5 text-sm outline-none"
          onBlur={(e) => {
            onCellChange(rec, col.key, e.target.value);
            setEditing(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") setEditing(null);
          }}
          onPaste={(e) => {
            const t = e.clipboardData.getData("text");
            if (t && /\r?\n/.test(t.trim())) {
              e.preventDefault();
              const lines = t.replace(/\r/g, "").split("\n");
              while (lines.length && lines[lines.length - 1] === "") lines.pop();
              onFillColumn(col.key, rec.id, lines);
              setEditing(null);
            }
          }}
          data-testid={`cell-input-${col.key}`}
        />
      );
    }

    const display =
      col.type === "number" && val !== "" && !isNaN(val)
        ? Number(val).toLocaleString("tr-TR")
        : val;

    return (
      <div
        className={`truncate ${canEdit ? "cursor-text" : ""} ${
          col.key === "id" ? "font-mono text-xs text-muted-foreground" : ""
        }`}
        onClick={() => canEdit && setEditing({ rowId: rec.id, key: col.key })}
        title={String(val)}
      >
        {display !== "" ? display : <span className="text-muted-foreground/40">—</span>}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto grid-scroll border border-border rounded-xl bg-card">
      <table className="border-collapse" style={{ minWidth: "100%" }}>
        <thead className="sticky top-0 z-20">
          <tr>
            <th className="sticky left-0 z-30 bg-secondary border-b border-r border-border px-2 py-2.5 w-10">
              <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all-rows" />
            </th>
            {visible.map((col) => (
              <th
                key={col.key}
                draggable
                onDragStart={() => (dragKey.current = col.key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragKey.current && dragKey.current !== col.key)
                    onReorder(dragKey.current, col.key);
                  dragKey.current = null;
                }}
                className="relative bg-secondary border-b border-r border-border px-3 py-2.5 text-left group"
                style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                data-testid={`col-header-${col.key}`}
              >
                <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <GripVertical className="w-3 h-3 opacity-0 group-hover:opacity-60 cursor-grab flex-shrink-0" />
                  <span className="truncate flex-1">{col.label}</span>
                  {canManageCols && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-0 group-hover:opacity-100 hover:text-foreground flex-shrink-0"
                          data-testid={`col-menu-${col.key}`}
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onHideColumn(col.key)}>
                          <EyeOff className="w-4 h-4 mr-2" /> Gizle
                        </DropdownMenuItem>
                        {col.key !== "id" && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => onDeleteColumn(col)}
                            data-testid={`col-delete-${col.key}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Sütunu Sil
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div
                  onMouseDown={(e) => startResize(e, col.key, col.width)}
                  className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/50"
                  data-testid={`col-resize-${col.key}`}
                />
              </th>
            ))}
            {showActions && (
              <th className="bg-secondary border-b border-border px-3 py-2.5 w-28" />
            )}
          </tr>
          {showFilters && (
            <tr>
              <th className="sticky left-0 z-30 bg-card border-b border-r border-border p-1" />
              {visible.map((col) => (
                <th
                  key={col.key}
                  className="bg-card border-b border-r border-border p-1"
                  style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                >
                  {col.key === "il" || col.key === "ilce" ? (
                    <select
                      value={filters[col.key] || ""}
                      onChange={(e) => onFilterChange(col.key, e.target.value)}
                      className="w-full text-xs bg-background border border-border rounded px-1 py-1 outline-none"
                      data-testid={`filter-${col.key}`}
                    >
                      <option value="">Tümü</option>
                      {(col.key === "il" ? PROVINCES : getDistricts(filters.il)).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : col.type === "select" ? (
                    <select
                      value={filters[col.key] || ""}
                      onChange={(e) => onFilterChange(col.key, e.target.value)}
                      className="w-full text-xs bg-background border border-border rounded px-1 py-1 outline-none"
                      data-testid={`filter-${col.key}`}
                    >
                      <option value="">Tümü</option>
                      {(col.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.value}
                        </option>
                      ))}
                    </select>
                  ) : col.type === "number" ? (
                    (() => {
                      const fo = typeof filters[col.key] === "object" && filters[col.key] ? filters[col.key] : {};
                      const setFo = (patch) => onFilterChange(col.key, { op: fo.op || ">=", ...fo, ...patch });
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            <select
                              value={fo.op || ">="}
                              onChange={(e) => setFo({ op: e.target.value })}
                              className="w-11 text-xs bg-background border border-border rounded px-0.5 py-1 outline-none"
                              data-testid={`filter-op-${col.key}`}
                            >
                              <option value=">=">≥</option>
                              <option value="<=">≤</option>
                              <option value="=">=</option>
                              <option value=">">&gt;</option>
                              <option value="<">&lt;</option>
                              <option value="between">↔</option>
                            </select>
                            <input
                              type="number"
                              value={fo.val ?? ""}
                              onChange={(e) => setFo({ val: e.target.value })}
                              placeholder="Değer"
                              className="w-full min-w-0 text-xs bg-background border border-border rounded px-1 py-1 outline-none"
                              data-testid={`filter-${col.key}`}
                            />
                          </div>
                          {fo.op === "between" && (
                            <input
                              type="number"
                              value={fo.val2 ?? ""}
                              onChange={(e) => setFo({ val2: e.target.value })}
                              placeholder="Üst sınır"
                              className="w-full min-w-0 text-xs bg-background border border-border rounded px-1 py-1 outline-none"
                              data-testid={`filter-max-${col.key}`}
                            />
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <input
                      value={filters[col.key] || ""}
                      onChange={(e) => onFilterChange(col.key, e.target.value)}
                      placeholder="Filtre"
                      className="w-full text-xs bg-background border border-border rounded px-1 py-1 outline-none"
                      data-testid={`filter-${col.key}`}
                    />
                  )}
                </th>
              ))}
              {showActions && <th className="bg-card border-b border-border" />}
            </tr>
          )}
        </thead>
        <tbody>
          {records.map((rec, ri) => (
            <tr key={rec.id} className="hover:bg-secondary/40" data-testid={`row-${ri}`}>
              <td className="sticky left-0 z-10 bg-card border-b border-r border-border px-2 py-2 text-center">
                <Checkbox
                  checked={selected.includes(rec.id)}
                  onCheckedChange={() => toggleOne(rec.id)}
                  data-testid={`select-row-${ri}`}
                />
              </td>
              {visible.map((col) => (
                <td
                  key={col.key}
                  className="border-b border-r border-border px-3 py-1.5 text-sm align-middle"
                  style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                >
                  {renderCell(rec, col)}
                </td>
              ))}
              {showActions && (
                <td className="border-b border-border px-2 py-1.5 text-center whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 relative"
                    onClick={() => onOpenNotes(rec)}
                    title="Görüşme notları"
                    data-testid={`notes-row-${ri}`}
                  >
                    {rec.note_count > 0 ? (
                      <span className="text-base leading-none">📝</span>
                    ) : (
                      <MessageSquarePlus className="w-4 h-4 text-muted-foreground/60" />
                    )}
                    {rec.note_count > 0 && (
                      <span
                        className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] px-1 grid place-items-center"
                        data-testid={`notes-count-${ri}`}
                      >
                        {rec.note_count}
                      </span>
                    )}
                  </Button>
                  {canEdit && onArchiveRow && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                      onClick={() => onArchiveRow(rec)}
                      title={view === "archived" ? "Arşivden çıkar" : "Arşivle"}
                      data-testid={`archive-row-${ri}`}
                    >
                      {view === "archived" ? (
                        <ArchiveRestore className="w-4 h-4" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => onDeleteRow(rec)}
                      data-testid={`delete-row-${ri}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {records.length === 0 && (
            <tr>
              <td colSpan={visible.length + 2} className="text-center py-16 text-muted-foreground">
                {view === "archived"
                  ? "Arşivde kayıt yok."
                  : "Kayıt bulunamadı. Excel yükleyin veya yeni kayıt ekleyin."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
